import { Center, Modal, Box, VStack } from 'native-base';
import { useGeneralStyles, useItemCardStyles, useModalStyles, useStoreStyles } from './styles';
import { useI18n, useSpringBigStore, pluralize } from '@springbig/shared';
import React, { useEffect, useRef, useState } from 'react';
import { Caption, H3, H4 } from './Typography';
import CompactImagesGallery from './CompactImagesGallery';
import Button from '~/shared/Button';
import SimpleProgressBar from '~/shared/SimpleProgressBar';
import { SpIcon } from '~/shared/Icon';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import Pressable from './Pressable';

export type ModalProps = {
    showModal: boolean;
    extensionId: number;
    type: 'rewards' | 'offers';
    onCloseModal: () => void;
    onRedeem: () => void;
    isInStore?: boolean;
};

const ExtensionDetailsModal = ({
    showModal,
    type,
    extensionId,
    onCloseModal,
    onRedeem,
    isInStore
}: ModalProps) => {

    const { t } = useI18n();

    const {
        star_icon_color
    } = useItemCardStyles();

    const {
        link_text
    } = useStoreStyles();

    const {
        text,
        header_background,
        body_background,
        footer_background,
    } = useModalStyles();
    const gStyle = useGeneralStyles();

    const { member } = useSpringBigStore();

    const modalRef = useRef();

    const [modalWidth, setModalWidth] = useState(null);
    const [reward, setReward] = useState(null);
    const [images, setImages] = useState(null);

    useEffect(() => {

        if (!member || !member.rewards) {
            return;
        }

        if (type === 'rewards') {
            setReward(member.rewards.find(targetReward => targetReward.id === extensionId));
        }
        else {
            setReward(member.reward_grants.find(targetReward => targetReward.id === extensionId)?.reward);
        }
    }, [extensionId]);

    useEffect(() => {

        if (!reward) {
            return;
        }

        setImages(reward?.images.filter(image => image.label === 'preload'));
    }, [reward]);

    useEffect(() => {

        if (modalRef.current) {
            setModalWidth((modalRef.current as HTMLDivElement).offsetWidth);
        }
    });

    useEffect(() => {

        const handleResize = () => {

            if (modalRef.current) {
                setModalWidth((modalRef.current as HTMLDivElement).offsetWidth);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {

            window.removeEventListener('resize', handleResize);
        };
    }, [modalRef.current]);

    const handleRedeem = (): void => {

        onRedeem();
    };

    const handleCloseModal = (): void => {

        onCloseModal();
    };

    const handleLinkPress = (url: string): void => {

        if (!url) {
            return;
        }

        window.open(url, '_blank');
    };

    return (
        reward && (
            <Center>
                <Modal isOpen={showModal} overflowY={'auto'}>
                    <Modal.Content ref={modalRef} position={'relative'}>
                        { images && images.length ? (
                            <Modal.Header
                                p={0}
                                flexDirection={'row'}
                                bg={header_background}
                            >
                                <CompactImagesGallery imagesProp={images} width={modalWidth} />
                            </Modal.Header>
                        ) : (
                            <Box
                                w={modalWidth / 2}
                                h={modalWidth / 2}
                                mx={'auto'}
                                alignItems={'center'}
                                justifyContent={'center'}
                            >
                                <SpIcon
                                    icon={faStar}
                                    size={modalWidth / 2}
                                    color={star_icon_color}
                                />
                            </Box>
                        )}
                        <Modal.Body
                            bg={body_background}
                            textAlign={'center'}
                            h={'100%'}
                        >
                            <VStack space={4}>
                                <Box>
                                    <H3 color={text}>{ reward.name }</H3>
                                    { type === 'rewards' &&
                                        <Caption opacity={.75}>
                                            { reward.item_value } { pluralize(reward.item_value, 'point') }
                                        </Caption>
                                    }
                                    { type === 'offers' && reward.expiration_date &&
                                        <Caption opacity={.75}>
                                            expires { reward.expiration_date }
                                        </Caption>
                                    }
                                </Box>
                                { reward.description &&
                                    <Caption>{ reward.description }</Caption>
                                }
                                { reward?.product_url_attributes?.value &&
                                    <Pressable onPress={() => handleLinkPress(reward.product_url_attributes.value)}>
                                        <H4 color={link_text}>
                                            { reward.product_url_attributes.name }
                                        </H4>
                                    </Pressable>
                                }
                                { reward?.offer_effects_attributes?.length > 0 &&
                                    <Box mb={4}>
                                        { reward.offer_effects_attributes.map(effect =>

                                            <SimpleProgressBar key={effect.id} progress={effect.value} title={effect.name} />
                                        )}
                                    </Box>
                                }
                                { !isInStore &&
                                    <Caption>
                                        { t(`${type}.redemption_text`, [reward.name]) }
                                    </Caption>
                                }
                            </VStack>
                        </Modal.Body>
                        <Modal.Footer
                            bg={footer_background}
                            justifyContent={'center'}
                        >
                            <VStack
                                alignItems={'center'}
                                textAlign={'center'}
                                maxW={'100%'}
                            >
                                { !isInStore ? (member.balance >= reward.item_value ? (
                                    <Button
                                        testID='alert-accept'
                                        bg={gStyle.button_background}
                                        _text={{
                                            color: gStyle.button_text,
                                        }}
                                        onPress={() => handleRedeem()}
                                    >
                                        { `I'm with staff: proceed to ${type.slice(0, -1)}` }
                                    </Button>
                                ) : (
                                    <Caption>
                                        <Caption>you need </Caption>
                                        <Caption fontWeight='bold'>
                                            { reward.item_value } { pluralize(reward.item_value, 'point') }
                                        </Caption>
                                        <Caption> to redeem { reward.name}</Caption>
                                    </Caption>
                                )) :
                                    <VStack space={4}>
                                        <H3 color={text}>
                                            { t(`${type}.modal_title`) }
                                        </H3>
                                        <Caption
                                            color={text}
                                            w={'100%'}
                                            fontSize={12}
                                        >
                                            { t(`${type}.redemption_in_store`) }
                                        </Caption>
                                    </VStack>
                                }
                                <Button
                                    testID='alert-cancel'
                                    variant={'ghost'}
                                    bg={gStyle.button_neutral_background}
                                    whiteSpace={'nowrap'}
                                    _text={{
                                        color: text,
                                    }}
                                    onPress={() => handleCloseModal()}
                                >
                                    { `return to ${type}` }
                                </Button>
                            </VStack>
                        </Modal.Footer>
                    </Modal.Content>
                </Modal>
            </Center>
        )
    );
};

export default ExtensionDetailsModal;
